import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { http } from '@/lib/http';

export interface AuditRecord {
  auditUrn: string;
  ts: string;
  operatorUrn: string;
  operatorDisplayName: string;
  action: string;
  targetUrn?: string | null;
  correlationUrn: string;
  detailsMasked?: string | null;
  /** NEX-614: caseUrn for filtering entries by case. */
  caseUrn?: string | null;
}

/** Default page size — balances UX responsiveness with scroll depth. */
const PAGE_SIZE = 50;

/** Actions to exclude from the audit tab to reduce noise (#744). */
const FILTERED_ACTIONS = new Set([
  'PAYMENT_AUTHORIZED',
  'PAYMENT_CAPTURED',
]);

/** NEX-614: useAuditLog with pagination support.
 * Returns the first page by default (backward-compatible); use `useAuditLogPaginated`
 * for infinite scroll.
 */
export function useAuditLog(caseUrn: string) {
  return useQuery<AuditRecord[]>({
    queryKey: ['cases', caseUrn, 'audit'],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const records = await http.get(`v1/cases/${encodeURIComponent(caseUrn)}/audit?limit=${PAGE_SIZE}`).json<AuditRecord[]>();
      return records.filter((r) => !FILTERED_ACTIONS.has(r.action));
    },
    enabled: !!caseUrn,
  });
}

/** NEX-614: Paginated audit log with infinite scroll support. */
export function useAuditLogPaginated(caseUrn: string, options?: { excludePayments?: boolean }) {
  const excludePayments = options?.excludePayments ?? true;

  return useInfiniteQuery<AuditRecord[], Error>({
    queryKey: ['cases', caseUrn, 'audit', 'paginated', { excludePayments }],
    staleTime: 5 * 60 * 1000,
    queryFn: async ({ pageParam = 0 }) => {
      const offset = typeof pageParam === 'number' ? pageParam : 0;
      const records = await http
        .get(`v1/cases/${encodeURIComponent(caseUrn)}/audit?limit=${PAGE_SIZE}&offset=${offset}`)
        .json<AuditRecord[]>();
      return excludePayments ? records.filter((r) => !FILTERED_ACTIONS.has(r.action)) : records;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < PAGE_SIZE) return undefined;
      return allPages.reduce((acc, page) => acc + page.length, 0);
    },
    enabled: !!caseUrn,
  });
}
